import { LINKS } from "../../lib/links";
import {
  ArrowUpRight,
  ShieldCheck,
  Mail,
  Lock,
  Check,
} from "lucide-react";

const VALUE_STACK = [
  "Full-day intensive · 9AM → 5PM",
  "6 focused workshop blocks",
  "Professional content & headshot shoot",
  "Personalised 30-day execution plan",
  "Networking with ambitious builders",
  "30 days of accountability follow-up",
];

const TRUST = [
  { icon: Check, label: "Instant spot reservation" },
  { icon: Mail, label: "Automated confirmation email" },
  { icon: Lock, label: "Secure checkout · Stripe" },
];

export const BuildDayApplication = () => {
  return (
    <section
      id="apply-buildday"
      data-testid="buildday-apply-section"
      className="relative bg-[#0A0A0C] py-24 md:py-40 border-t border-zinc-800 overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-50 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 50% 40%, rgba(79,124,255,0.18), transparent 60%)",
        }}
      />

      <div className="relative max-w-5xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12 md:mb-16">
          <div className="reveal flex items-center justify-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#FCD34D]" />
            <span className="text-xs tracking-[0.3em] uppercase text-[#FCD34D] font-bold">
              Limited Spaces · One Day Only
            </span>
            <span className="h-px w-10 bg-[#FCD34D]" />
          </div>
          <h2
            data-testid="buildday-apply-headline"
            className="reveal text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-extrabold text-white leading-[1.02]"
          >
            Reserve Your{" "}
            <span className="italic font-light text-white">Spot.</span>
          </h2>
          <p
            data-testid="buildday-apply-subheadline"
            className="reveal mt-6 max-w-2xl mx-auto text-base md:text-lg leading-relaxed text-zinc-300 font-medium"
          >
            One transaction. One confirmation. One day that changes how you
            build. Reserve directly — no forms, no waiting, no friction.
          </p>
        </div>

        {/* Reserve card */}
        <div
          data-testid="buildday-reserve-card"
          className="reveal relative bg-[#15161B] border border-zinc-700 p-8 md:p-12 shadow-2xl"
        >
          <div className="absolute -top-3 left-6 bg-[#0A0A0C] px-3 text-[10px] tracking-[0.3em] uppercase text-[#FCD34D] font-bold">
            The Builders Sprint · Reservation
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-7">
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 font-bold mb-3">
                One-Day Intensive · FMF Studio · London
              </div>
              <h3 className="text-2xl md:text-3xl tracking-tight font-bold text-white leading-tight mb-5">
                Everything you walk away with.
              </h3>
              <p className="text-sm md:text-base text-zinc-300 leading-relaxed mb-8 max-w-md">
                Built to give you a real transformation in a single day. Your
                seat is held the moment checkout completes — confirmation,
                schedule and venue details land in your inbox instantly.
              </p>

              <ul className="space-y-3 mb-2">
                {VALUE_STACK.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-sm md:text-base text-zinc-200"
                  >
                    <span className="inline-flex items-center justify-center w-5 h-5 mt-0.5 border border-[#4F7CFF]/50 bg-[#4F7CFF]/10 flex-shrink-0">
                      <Check className="w-3 h-3 text-[#4F7CFF]" strokeWidth={2.5} />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-5 flex flex-col gap-4 md:sticky md:top-28">
              <div className="bg-[#0E0F14] border border-zinc-800 p-6">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#FCD34D] font-bold mb-2">
                  Pay now · Spot secured
                </div>
                <p className="text-sm text-zinc-300 leading-relaxed mb-6">
                  Skip the queue. Spaces are filled in order of payment — once
                  full, applications close until the next cohort.
                </p>
                <a
                  href={LINKS.buildDayCheckout}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="buildday-reserve-cta"
                  className="group w-full inline-flex items-center justify-center gap-3 bg-white text-black font-bold text-sm tracking-[0.15em] uppercase px-6 py-5 hover:bg-zinc-100 transition-colors duration-300"
                >
                  Reserve Your Spot
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>

                <ul className="mt-6 space-y-2.5">
                  {TRUST.map((t) => {
                    const Icon = t.icon;
                    return (
                      <li
                        key={t.label}
                        className="flex items-center gap-2.5 text-xs text-zinc-400"
                      >
                        <Icon className="w-3.5 h-3.5 text-[#4F7CFF]" strokeWidth={1.8} />
                        <span>{t.label}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <a
                href={LINKS.email}
                data-testid="buildday-questions-link"
                className="group inline-flex items-center justify-center gap-2 text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-white transition-colors font-bold"
              >
                <Mail className="w-3.5 h-3.5" strokeWidth={1.6} />
                Have a question? Email Jordan
              </a>
            </div>
          </div>
        </div>

        <div className="reveal mt-10 flex items-center justify-center gap-3 text-zinc-400">
          <ShieldCheck className="w-4 h-4 text-[#4F7CFF]" strokeWidth={1.6} />
          <p className="text-xs md:text-sm tracking-[0.15em] uppercase font-bold">
            Confirmation sent automatically · Venue + schedule included
          </p>
        </div>
      </div>
    </section>
  );
};
